import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { Keyboard } from 'react-native';
import {
    ControlledInput,
    ControlledInputTrigger,
    Form,
    USE_FORM_MODE_DEFAULTS,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { useFlow } from '../../common/providers/FlowProvider';
import { useFlowRouter } from '../../common/providers/FlowRouter';
import { BaseStep } from '../../common/Step';
import type { DeepPartial } from '../../common/types/DeepPartial';
import { getFirstUser } from '../../common/util/userUtils';
import type { AddIncomeFlowSteps } from '../AddIncomeFlow';
import IncomeBelongToSelectionSheet from '../sheets/IncomeOwnerSelectionSheet';
import IncomeTypeSelectionSheet from '../sheets/IncomeTypeSelectionSheet';
import { type Income, type IncomeType } from '../types/Income';
import {
    formatIncomeType,
    getIncomeOwnerDisplayFormatter,
} from '../utils/incomeUtils';

const incomeSourceSchema = z.object({
    description: z.string().min(2, 'Required field'),
    belongsToUserId: z.string().min(1, 'Required field'),
    type: z.string().min(1, 'Required field'),
});

type IncomeSourceSchema = z.infer<typeof incomeSourceSchema>;

interface IncomeSourceStepProps {
    shouldUseCurrentUserAsIncomeOwner?: boolean;
}

export const IncomeSourceStep = ({
    shouldUseCurrentUserAsIncomeOwner = false,
}: IncomeSourceStepProps) => {
    const { data, membership, logger, currentUser } = useFlow<Income>();
    const { onContinue } = useFlowRouter<Income, AddIncomeFlowSteps>();

    const [isIncomeOwnerSheetOpen, setIsIncomeOwnerSheetOpen] = useState(false);
    const [isIncomeTypeSheetOpen, setIsIncomeTypeSheetOpen] = useState(false);

    const { users } = membership;
    const firstUser = getFirstUser(users, currentUser);

    const isSoloMembership = users.length === 1;

    // If shouldUseCurrentUserAsIncomeOwner override or only one user in the membership, default to that user as the Income Owner.
    const defaultIncomeOwner =
        shouldUseCurrentUserAsIncomeOwner || isSoloMembership
            ? firstUser
            : null;

    const shouldShowIncomeOwnerInput =
        !isSoloMembership && !shouldUseCurrentUserAsIncomeOwner;

    const formatIncomeOwner = getIncomeOwnerDisplayFormatter(users, logger);

    const {
        register,
        control,
        handleSubmit,
        setFocus,
        setValue,
        formState: { errors, isValid },
        trigger,
        watch,
    } = useForm<IncomeSourceSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(incomeSourceSchema),
        defaultValues: {
            description: data.description ?? '',
            belongsToUserId:
                data.belongsToUserId ?? defaultIncomeOwner?.id ?? '',
            type: data.type ?? '',
        },
    });

    const belongsToUserId = watch('belongsToUserId');
    const type = watch('type');

    const onSubmit: SubmitHandler<IncomeSourceSchema> = async ({
        description,
        belongsToUserId,
        type,
    }: IncomeSourceSchema) => {
        let incomeData: DeepPartial<Income> = {
            description,
            belongsToUserId,
            type: type as IncomeType,
        };

        // If we're updating an existing income, and the type is changing, reset all of the IncomeType-specific fields.
        if (data.id != null && data.type !== (type as IncomeType)) {
            incomeData = {
                annualSalaryCents: null,
                payPerHourCents: null,
                monthlyTakeHomeCents: null,
                weeklyTakeHomeCents: null,
                weeklyHours: null,
                payCycle: null,
                additionalCompensation: [],
                ...incomeData,
            };
        }

        await onContinue(incomeData);
    };

    return (
        <BaseStep
            onContinue={handleSubmit(onSubmit)}
            isContinueDisabled={!isValid}
        >
            <Form onSubmit={handleSubmit(onSubmit)} marginBottom={12}>
                <YStack gap={12}>
                    <ControlledInput
                        label="Job title"
                        name="description"
                        placeholder="Give this income a name"
                        fontWeight="regular"
                        control={control}
                        register={register}
                        errors={errors}
                        autoCapitalize="words"
                        enterKeyHint="next"
                        onSubmitEditing={() => {
                            Keyboard.dismiss();
                            if (shouldShowIncomeOwnerInput) {
                                setFocus('belongsToUserId');
                                setIsIncomeOwnerSheetOpen(true);
                            } else {
                                setFocus('type');
                                setIsIncomeTypeSheetOpen(true);
                            }
                        }}
                    />
                    {shouldShowIncomeOwnerInput && (
                        <ControlledInputTrigger
                            label="Income owner"
                            name="belongsToUserId"
                            formatValue={formatIncomeOwner}
                            control={control}
                            register={register}
                            errors={errors}
                            onTrigger={() => {
                                setIsIncomeOwnerSheetOpen(true);
                            }}
                        />
                    )}
                    <ControlledInputTrigger
                        label="Type of income"
                        name="type"
                        formatValue={formatIncomeType}
                        control={control}
                        register={register}
                        errors={errors}
                        onTrigger={() => {
                            setIsIncomeTypeSheetOpen(true);
                        }}
                    />
                </YStack>
            </Form>
            <IncomeBelongToSelectionSheet
                isOpen={isIncomeOwnerSheetOpen}
                selectedOption={belongsToUserId}
                onSelectOption={(belongsToUserId: string | null) => {
                    if (belongsToUserId != null) {
                        setValue('belongsToUserId', belongsToUserId);
                        void trigger('belongsToUserId'); // Manually trigger form validation after setValue
                    }
                }}
                onClose={() => {
                    setIsIncomeOwnerSheetOpen(false);
                    setFocus('type');
                    setIsIncomeTypeSheetOpen(true);
                }}
            />
            <IncomeTypeSelectionSheet
                isOpen={isIncomeTypeSheetOpen}
                selectedOption={type as IncomeType}
                onSelectOption={(type: IncomeType | null) => {
                    if (type != null) {
                        setValue('type', type);
                        void trigger('type'); // Manually trigger form validation after setValue
                    }
                }}
                onClose={() => {
                    setIsIncomeTypeSheetOpen(false);
                }}
            />
        </BaseStep>
    );
};
